import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiGet } from '../services/apiClient';

interface Country {
  name: string;
  alpha2Code: string;
  alpha3Code: string;
  capital?: string;
  region?: string;
  subregion?: string;
  population?: number;
  flag?: string;
  languages?: Array<{ name: string; nativeName?: string }>;
  currencies: Array<{
    code: string;
    name: string;
    symbol: string;
  }>;
}

interface CountryDataContextType {
  useStaticData: boolean;
  setUseStaticData: (value: boolean) => void;
  countries: Country[];
  loading: boolean;
  error: string | null;
}

const CountryDataContext = createContext<CountryDataContextType>({
  useStaticData: false,
  setUseStaticData: () => {},
  countries: [],
  loading: false,
  error: null,
});

export const useCountryData = () => useContext(CountryDataContext);

export const CountryDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [useStaticData, setUseStaticData] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (useStaticData) {
          const storedData = localStorage.getItem('countriesData');
          if (storedData) {
            setCountries(JSON.parse(storedData));
          } else {
            setError('No static data available. Please upload data in the admin panel.');
            setCountries([]);
          }
        } else {
          // Country data is proxied + cached by the backend (no third-party call here).
          const data = await apiGet<Country[]>('/api/countries');
          setCountries(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        setError('Error fetching country data');
        setCountries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [useStaticData]);

  return (
    <CountryDataContext.Provider 
      value={{ 
        useStaticData, 
        setUseStaticData, 
        countries, 
        loading, 
        error 
      }}
    >
      {children}
    </CountryDataContext.Provider>
  );
}; 